import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFontSize, FontSize } from '../hooks/useFontSize';
import ContactSellerModal from '../components/ContactSellerModal';
import ImageAttributionModal from '../components/ImageAttributionModal';
import { ItemGrid } from '../components/inventory/ItemGrid';
import { MarketplaceItemCard } from '../components/marketplace/MarketplaceItemCard';
import { ContextHelpTip } from '../components/ContextHelpTip';
import { sizeToLayout, ThumbnailSize } from '../types/viewComposition';
import '../styles/pages/guestCollectionView.css';
import '../styles/header.css';
import '../styles/components/unifiedHeader.css';
import '../styles/pages/marketplacePage.css';
import '../styles/pages/auctionDetailPage.css';
import '../styles/components/itemGrid.css';
import '../styles/components/itemCard.css';
import API_BASE_URL from '../config';

type Filter = 'all' | 'auctions' | 'depots';
const FILTER_LABELS: Record<Filter, string> = {
  all: 'All',
  auctions: 'Auctions',
  depots: 'Depots',
};

const MarketplacePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fontSize, setFontSize } = useFontSize();

  /* ---------- URL-driven highlight (from shared auction links) ---------- */
  const highlightAuctionId = searchParams.get('auction_item') ? Number(searchParams.get('auction_item')) : null;
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const didScrollRef = useRef(false);

  /* ---------- URL-driven deep-link: ?depot=X&item=Y,Z ---------- */
  const linkDepotId = searchParams.get('depot') ? Number(searchParams.get('depot')) : null;
  const linkItemIds = searchParams.get('item')
    ? searchParams.get('item')!.split(',').map(Number).filter(n => !isNaN(n))
    : [];
  const didDeepLinkRef = useRef(false);

  /* ---------- state ---------- */
  const [filter, setFilter] = useState<Filter>(highlightAuctionId ? 'all' : 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [auctionItems, setAuctionItems] = useState<any[]>([]);
  const [depotItems, setDepotItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedDescs, setExpandedDescs] = useState<Set<string>>(new Set());

  // Depot drill-down
  const [selectedDepot, setSelectedDepot] = useState<any>(null);
  const [drillDownItems, setDrillDownItems] = useState<any[]>([]);
  const [drillDownLoading, setDrillDownLoading] = useState(false);

  // Sign-in prompt
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);

  // Share link toast
  const [showCopiedToast, setShowCopiedToast] = useState(false);

  // Purchase flow — depot item selection + review sheet + contact seller
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  // Map of depot item id → buy_now_price (cents) for items selected via Buy Now
  const [buyNowPrices, setBuyNowPrices] = useState<Map<number, number>>(new Map());
  const [showReviewSheet, setShowReviewSheet] = useState(false);
  const [reviewItems, setReviewItems] = useState<Array<{ id: number; name: string; qty: number; maxQty: number; price: number; originalPrice: number; priceLocked?: boolean; sellerId: number; sellerName: string; depotId: number }>>([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactReviewItems, setContactReviewItems] = useState<Array<{ id: number; name: string; qty: number; maxQty: number; price: number; originalPrice: number; priceLocked?: boolean }> | undefined>(undefined);
  const [contactSellerId, setContactSellerId] = useState<number>(0);
  const [contactSellerName, setContactSellerName] = useState<string>('');
  const [contactDepotId, setContactDepotId] = useState<number>(0);

  // Image attribution modal
  const [attrImage, setAttrImage] = useState<{ url: string; name: string; photographerName?: string; photographerUsername?: string; unsplashPhotoId?: string } | null>(null);

  // Proximity search
  const DISTANCE_PRESETS = [5, 10, 25, 50, 100];
  const [viewerLoc, setViewerLoc] = useState<{ lat: number; lng: number } | null>(() => {
    const saved = localStorage.getItem('marketplace-viewer-loc');
    if (saved) { try { return JSON.parse(saved); } catch { return null; } }
    return null;
  });
  const [maxDistance, setMaxDistance] = useState<number | null>(() => {
    const saved = localStorage.getItem('marketplace-max-distance');
    return saved ? Number(saved) : null;
  });
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [locatingBrowser, setLocatingBrowser] = useState(false);
  const [customDistanceInput, setCustomDistanceInput] = useState('');

  // Proximity helpers
  const saveViewerLoc = (loc: { lat: number; lng: number }) => {
    setViewerLoc(loc);
    localStorage.setItem('marketplace-viewer-loc', JSON.stringify(loc));
    setLocationError(null);
  };

  const handleBrowserLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser.');
      return;
    }
    setLocatingBrowser(true);
    setLocationError(null);
    // Manual timeout guard — some mobile browsers never fire the error callback
    const fallbackTimer = setTimeout(() => {
      setLocatingBrowser(false);
      setLocationError('Location request timed out. Try entering a zip code instead.');
    }, 15000);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(fallbackTimer);
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        saveViewerLoc(loc);
        setLocatingBrowser(false);
        setCustomDistanceInput('');
        fetchData(loc);
      },
      (err) => {
        clearTimeout(fallbackTimer);
        setLocatingBrowser(false);
        setLocationError(
          err.code === 1 ? 'Location access denied. Please allow location access or enter a zip code.'
          : 'Could not determine your location. Try entering a zip code instead.'
        );
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  const handleZipLookup = async (zip: string) => {
    if (!/^\d{5}$/.test(zip.trim())) {
      setLocationError('Please enter a valid 5-digit US zip code.');
      return;
    }
    setLocationError(null);
    setLocatingBrowser(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip.trim()}`);
      if (!res.ok) { setLocationError('Zip code not found. Please try another.'); setLocatingBrowser(false); return; }
      const data = await res.json();
      const place = data.places?.[0];
      if (!place) { setLocationError('Zip code not found.'); setLocatingBrowser(false); return; }
      const loc = { lat: parseFloat(place.latitude), lng: parseFloat(place.longitude) };
      saveViewerLoc(loc);
      setLocatingBrowser(false);
      setCustomDistanceInput('');
      fetchData(loc);
    } catch {
      setLocationError('Failed to look up zip code. Please try again.');
      setLocatingBrowser(false);
    }
  };

  const handleProximityButtonClick = () => {
    if (!viewerLoc) {
      setShowLocationModal(true);
    } else if (maxDistance) {
      // Toggle off — clear distance filter
      setMaxDistance(null);
      localStorage.removeItem('marketplace-max-distance');
    } else {
      setShowLocationModal(true);
    }
  };

  const selectDistance = (miles: number) => {
    setMaxDistance(miles);
    localStorage.setItem('marketplace-max-distance', String(miles));
    setShowLocationModal(false);
    if (!viewerLoc) {
      // Shouldn't happen — location should be set first
      setShowLocationModal(true);
    }
  };

  const clearProximity = () => {
    setMaxDistance(null);
    setViewerLoc(null);
    localStorage.removeItem('marketplace-max-distance');
    localStorage.removeItem('marketplace-viewer-loc');
    fetchData(null);
  };

  const getItemMiles = (item: any): number | null => {
    if (item._type === 'auction') return item.distance_miles ?? null;
    if (item._type === 'depot' && item.distance?.miles != null) return item.distance.miles;
    return null;
  };

  // Lock body scroll when review sheet is open
  useEffect(() => {
    if (showReviewSheet) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [showReviewSheet]);

  // Guest-local theme toggle — default to system preference, not the logged-in user's stored theme
  const [guestTheme, setGuestTheme] = useState<'light' | 'dark'>(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const savedThemePrefRef = useRef<string | null>(null);
  useEffect(() => {
    // Save the logged-in user's theme preference so we can restore it on unmount
    savedThemePrefRef.current = localStorage.getItem('theme-preference');
    // Temporarily override localStorage so ThemeProvider resolves to system theme
    localStorage.setItem('theme-preference', 'system');
    // Apply guest theme immediately
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', systemTheme);
    return () => {
      if (savedThemePrefRef.current !== null) {
        localStorage.setItem('theme-preference', savedThemePrefRef.current);
      } else {
        localStorage.removeItem('theme-preference');
      }
      document.documentElement.setAttribute('data-theme',
        savedThemePrefRef.current === 'dark' ? 'dark'
          : savedThemePrefRef.current === 'light' ? 'light'
          : savedThemePrefRef.current === 'pastel' ? 'pastel'
          : window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      );
    };
  }, []);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', guestTheme);
  }, [guestTheme]);

  /* ---------- data fetching ---------- */
  const fetchData = useCallback(async (loc?: { lat: number; lng: number } | null) => {
    setLoading(true);
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = token ? { 'Authorization': `Bearer ${token}` } : {};
    const coordParams = loc ? `&lat=${loc.lat}&lng=${loc.lng}` : '';

    try {
      const [auctionRes, depotRes] = await Promise.allSettled([
        fetch(`${API_BASE_URL}/api/auction-items/browse?sort=ending_soon${coordParams}`, { headers }),
        fetch(`${API_BASE_URL}/api/collections/marketplace/public/items?_=1${coordParams}`, { headers }),
      ]);

      if (auctionRes.status === 'fulfilled' && auctionRes.value.ok) {
        const data = await auctionRes.value.json();
        setAuctionItems(Array.isArray(data) ? data : []);
      }

      if (depotRes.status === 'fulfilled' && depotRes.value.ok) {
        const data = await depotRes.value.json();
        setDepotItems(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching marketplace data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(viewerLoc); }, [fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- deep-link: auto drill-down + pre-select ---------- */
  useEffect(() => {
    if (didDeepLinkRef.current || loading) return;
    if (!linkDepotId && linkItemIds.length === 0) return;
    didDeepLinkRef.current = true;
    // Pre-select linked items
    if (linkItemIds.length > 0) {
      setSelectedItems(new Set(linkItemIds));
    }
    // Drill into depot if specified
    if (linkDepotId) {
      openDepotDetail(linkDepotId, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  /* ---------- scroll to first linked item after data loads ---------- */
  useEffect(() => {
    if (linkItemIds.length > 0 && drillDownItems.length > 0 && highlightRef.current) {
      setTimeout(() => {
        highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillDownItems]);

  // Scroll for top-level pre-selected items (no depot drill-down)
  useEffect(() => {
    if (!linkDepotId && linkItemIds.length > 0 && !loading && highlightRef.current) {
      setTimeout(() => {
        highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  /* ---------- depot drill-down ---------- */
  const openDepotDetail = useCallback(async (depotId: number, depotName: string) => {
    setSelectedDepot({ depot_id: depotId, name: depotName });
    setDrillDownItems([]);
    setDrillDownLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = token ? { 'Authorization': `Bearer ${token}` } : {};
      const savedLoc = localStorage.getItem('marketplace-viewer-loc');
      const loc = savedLoc ? JSON.parse(savedLoc) : null;
      const coordQ = loc ? `?lat=${loc.lat}&lng=${loc.lng}` : '';
      const res = await fetch(`${API_BASE_URL}/api/collections/marketplace/public/${depotId}${coordQ}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setDrillDownItems(data.items || []);
        if (data.collection) {
          setSelectedDepot((prev: any) => ({ ...prev, ...data.collection }));
        }
      }
    } catch { /* ignore */ } finally {
      setDrillDownLoading(false);
    }
  }, []);

  const closeDepotDetail = () => {
    setSelectedDepot(null);
    setDrillDownItems([]);
  };

  /* ---------- depot item selection for purchase ---------- */
  const toggleItemSelection = (item: any) => {
    if ((item.quantity ?? 1) === 0) return;
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
      return next;
    });
  };

  const openReviewSheet = () => {
    // Merge both sources so selection works in main view and drill-down
    const allDepot = [...depotItems, ...drillDownItems];
    const seen = new Set<number>();
    const items = allDepot.filter(i => {
      if (!selectedItems.has(i.id) || seen.has(i.id)) return false;
      seen.add(i.id);
      return true;
    });
    if (items.length === 0) return;
    setReviewItems(items.map(i => {
      const bnPrice = buyNowPrices.get(i.id);
      const price = bnPrice != null ? bnPrice / 100 : (i.sale_price != null ? Number(i.sale_price) / 100 : 0);
      return {
        id: i.id,
        name: i.name,
        qty: 1,
        maxQty: i.quantity ?? 1,
        price,
        originalPrice: price,
        priceLocked: bnPrice != null,
        sellerId: i.owner_id ?? selectedDepot?.owner_id ?? 0,
        sellerName: i.owner_name ?? selectedDepot?.owner_name ?? 'Seller',
        depotId: i.depot_id ?? selectedDepot?.depot_id ?? 0,
      };
    }));
    setShowReviewSheet(true);
  };

  /* ---------- build unified items ---------- */
  // Build a map of item_id → depot item for cross-referencing
  const depotByItemId = new Map<number, any>();
  for (const d of depotItems) depotByItemId.set(d.id, d);

  const now = Date.now();
  // Set of item_ids that have a live (non-ended) auction — used to deduplicate depot items
  const auctionedItemIds = new Set(auctionItems.filter((a: any) => !a.ends_at || new Date(a.ends_at).getTime() > now).map((a: any) => a.item_id));
  const unifiedItems: any[] = [
    ...auctionItems.filter((item: any) => !item.ends_at || new Date(item.ends_at).getTime() > now).map((item: any) => {
      const base: any = { ...item, _type: 'auction' as const };
      // If this auction item also exists in a depot, enrich with depot metadata
      // so Buy Now items can be treated as selectable sale items
      const depot = depotByItemId.get(item.item_id);
      if (depot && item.buy_now_price != null) {
        base._depotItem = depot; // stash the depot data for contact flow
        base._hasBuyNow = true;
      }
      return base;
    }),
    // Filter out depot items that already appear as auction items
    ...depotItems
      .filter((item: any) => !auctionedItemIds.has(item.id))
      .map((item: any) => ({ ...item, _type: 'depot' as const })),
  ];

  const searchLower = searchTerm.toLowerCase().trim();
  const matchesSearch = (text: string | null | undefined) => !searchLower || (text || '').toLowerCase().includes(searchLower);

  const filteredItems = unifiedItems
    .filter((item) => {
      if (filter === 'auctions') return item._type === 'auction';
      if (filter === 'depots') return item._type === 'depot';
      return true;
    })
    .filter((item) =>
      matchesSearch(item.item_name) || matchesSearch(item.name) ||
      matchesSearch(item.depot_name) || matchesSearch(item.seller_name) ||
      matchesSearch(item.owner_name) || matchesSearch(item.item_description) ||
      matchesSearch(item.description)
    )
    .filter((item) => {
      if (!maxDistance || !viewerLoc) return true;
      const miles = getItemMiles(item);
      if (miles === null) return false; // no distance data — hide when filtering
      return miles <= maxDistance;
    });

  // Filtered drill-down items (search applies in depot view too)
  const filteredDrillDownItems = drillDownItems.filter((item) =>
    matchesSearch(item.name) || matchesSearch(item.description)
  );

  /* ---------- scroll to highlighted item ---------- */
  useEffect(() => {
    if (highlightAuctionId && highlightRef.current && !didScrollRef.current && !loading) {
      didScrollRef.current = true;
      setTimeout(() => {
        highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [highlightAuctionId, loading]);

  /* ---------- layout from font size (combined control) ---------- */
  const thumbSize = (fontSize || 'medium') as ThumbnailSize;
  const thumbLayout = sizeToLayout(thumbSize);

  const handlePublicImageClick = (item: any) => {
    const itemName = item._type === 'depot' ? item.name : (item.item_name || item.name);
    if (item.image_id) {
      setAttrImage({
        url: `${API_BASE_URL}/api/images/${item.image_id}/public`,
        name: itemName,
        photographerName: item.photographer_name,
        photographerUsername: item.photographer_username,
        unsplashPhotoId: item.unsplash_photo_id,
      });
    }
  };

  const handlePublicItemClick = (item: any) => {
    if (item._type === 'depot') {
      toggleItemSelection(item);
    } else if (item._hasBuyNow && item._depotItem) {
      // Auction with Buy Now + depot listing → treat as selectable sale item
      const depotItem = item._depotItem;
      const depotId = depotItem.id;
      setBuyNowPrices(prev => {
        const next = new Map(prev);
        if (next.has(depotId)) next.delete(depotId); else next.set(depotId, item.buy_now_price);
        return next;
      });
      toggleItemSelection(depotItem);
    } else if (item._type === 'auction') {
      // Pure auction (no Buy Now) → toggle highlight for share links
      toggleItemSelection(item);
    }
  };

  const handlePublicBidClick = (_item: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setShowSignInPrompt(true);
  };

  const handlePublicDepotClick = (item: any) => {
    openDepotDetail(item.depot_id, item.depot_name);
  };

  const toggleDesc = (key: string) => {
    setExpandedDescs(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  /* ---------- render ---------- */
  return (
    <div className="guest-collection-view">
      {/* Fixed Header */}
      <div
        ref={(el) => {
          if (el) {
            const h = el.getBoundingClientRect().height;
            document.documentElement.style.setProperty('--guest-header-h', `${Math.ceil(h)}px`);
          }
        }}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, background: 'var(--color-bg-primary)', borderBottom: '1px solid var(--color-border)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 1rem', position: 'relative' }}>
          <img src="/happy_sock.png" alt="Wandering Sock" className="unified-header-icon" />
          <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <div style={{ textAlign: 'center' }}>
              <div className="unified-header-title">Marketplace</div>
              <div className="unified-header-subtitle">by Wandering Sock</div>
            </div>
          </div>
          <div className="unified-header-actions">
            <button
              onClick={() => {
                const sizes: FontSize[] = ['small', 'medium', 'large', 'x-large'];
                const currentIndex = sizes.indexOf(fontSize);
                const nextIndex = (currentIndex + 1) % sizes.length;
                setFontSize(sizes[nextIndex]);
              }}
              className="unified-header-font-toggle"
              title={`Font size: ${fontSize}`}
              aria-label="Change font size"
            >
              <span className="font-toggle-text">Aa</span>
              <span className="font-toggle-bars">
                <span className={`bar bar-1 ${['small','medium','large','x-large'].indexOf(fontSize) >= 0 ? 'active' : ''}`}></span>
                <span className={`bar bar-2 ${['medium','large','x-large'].indexOf(fontSize) >= 0 ? 'active' : ''}`}></span>
                <span className={`bar bar-3 ${['large','x-large'].indexOf(fontSize) >= 0 ? 'active' : ''}`}></span>
                <span className={`bar bar-4 ${fontSize === 'x-large' ? 'active' : ''}`}></span>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Action Bar: search + filter pills + theme toggle */}
      <div
        ref={(el) => {
          if (el) {
            const h = el.getBoundingClientRect().height;
            document.documentElement.style.setProperty('--guest-action-bar-h', `${Math.ceil(h)}px`);
          }
        }}
        style={{
          position: 'fixed',
          top: 'var(--guest-header-h, 70px)',
          left: 0,
          right: 0,
          zIndex: 99,
          background: 'var(--color-bg-primary)',
          padding: '8px 16px',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        {/* Search row */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div className="marketplace-bar-search" style={{ flex: 1 }}>
            <svg className="marketplace-bar-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="marketplace-bar-search-input"
              placeholder="Search items, depots, or sellers"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="marketplace-bar-search-clear"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
          {/* Theme toggle */}
          <button
            onClick={() => setGuestTheme(prev => prev === 'light' ? 'dark' : 'light')}
            style={{ background: 'transparent', border: 'none', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-secondary)', flexShrink: 0 }}
            title={`Theme: ${guestTheme}`}
            aria-label="Toggle theme"
          >
            {guestTheme === 'light' ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5"></circle>
                <line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                <line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
              </svg>
            )}
          </button>
          {/* Help */}
          <a
            href="https://wanderingsock.com/help"
            target="_blank"
            rel="noopener noreferrer"
            style={{ background: 'transparent', border: 'none', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-secondary)', flexShrink: 0, fontSize: '18px', fontWeight: 600, textDecoration: 'none', lineHeight: 1 }}
            title="Help"
            aria-label="Help"
          >
            ?
          </a>
        </div>

        {/* Filter pills row */}
        {!selectedDepot && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            {(Object.keys(FILTER_LABELS) as Filter[]).map((f) => {
              const count = f === 'all' ? unifiedItems.length
                : f === 'auctions' ? auctionItems.length
                : depotItems.length;
              return (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: filter === f ? 600 : 400,
                    border: filter === f ? '1.5px solid var(--color-accent)' : '1px solid var(--color-border)',
                    background: filter === f ? 'var(--color-accent-light, #e0f0ff)' : 'var(--color-bg-primary)',
                    color: filter === f ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {FILTER_LABELS[f]} {!loading && count > 0 && <span style={{ opacity: 0.7 }}>({count})</span>}
                </button>
              );
            })}
            {/* Proximity pill */}
            <button
              onClick={handleProximityButtonClick}
              style={{
                padding: '4px 12px',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: maxDistance ? 600 : 400,
                border: maxDistance ? '1.5px solid var(--color-accent)' : '1px solid var(--color-border)',
                background: maxDistance ? 'var(--color-accent-light, #e0f0ff)' : 'var(--color-bg-primary)',
                color: maxDistance ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                cursor: 'pointer',
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              {maxDistance ? `${maxDistance} mi` : '? Miles'}
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="guest-content" style={{ paddingTop: `calc(var(--guest-header-h, 70px) + var(--guest-action-bar-h, 90px) + 8px)`, paddingBottom: selectedItems.size > 0 ? '140px' : '60px' }}>
        {selectedDepot ? (
          /* ----- Depot drill-down view ----- */
          <>
            {/* Depot header */}
            <div style={{
              padding: '10px 16px',
              background: 'var(--color-bg-primary)',
              borderBottom: '1px solid var(--color-border)',
              position: 'sticky',
              top: 0,
              zIndex: 50,
              textAlign: 'center',
            }}>
              <button
                onClick={closeDepotDetail}
                style={{
                  position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 8,
                  color: 'var(--color-accent)', display: 'flex', alignItems: 'center',
                }}
                aria-label="Back"
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
              <div style={{ fontWeight: 700, fontSize: 'var(--font-size-base)', color: 'var(--color-accent)' }}>
                {selectedDepot.name}
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                Depot #{selectedDepot.depot_id} · {drillDownItems.length} {drillDownItems.length === 1 ? 'item' : 'items'}
              </div>
              {selectedDepot.description && (
                <>
                  <div style={{ width: '50%', margin: '8px auto 6px', borderBottom: '1px solid var(--color-border)' }} />
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textAlign: 'center' }}>
                    {selectedDepot.description}
                  </div>
                </>
              )}
            </div>

            {/* Depot items */}
            {drillDownLoading ? (
              <div className="marketplace-loading">Loading items...</div>
            ) : filteredDrillDownItems.length === 0 ? (
              <div className="marketplace-empty">
                {searchLower ? `No items matching "${searchTerm.trim()}"` : 'No items in this depot.'}
              </div>
            ) : (
              <ItemGrid layout={thumbLayout} size={thumbSize}>
                {filteredDrillDownItems.map((item: any) => {
                  const isFirstLinked = linkItemIds.includes(item.id) && linkItemIds[0] === item.id;
                  return (
                    <MarketplaceItemCard
                      key={item.id}
                      item={{ ...item, _type: 'depot' as const, depot_name: selectedDepot?.name, owner_name: selectedDepot?.owner_name, owner_location: selectedDepot?.owner_location, distance: selectedDepot?.distance }}
                      size={thumbSize}
                      onClick={() => toggleItemSelection(item)}
                      onImageClick={handlePublicImageClick}
                      onDepotClick={handlePublicDepotClick}
                      hideDepotName
                      isSelected={selectedItems.has(item.id)}
                      highlightRef={isFirstLinked ? highlightRef : undefined}
                      expandedDescs={expandedDescs}
                      onToggleDesc={toggleDesc}
                    />
                  );
                })}
              </ItemGrid>
            )}
          </>
        ) : (
          /* ----- Unified items list ----- */
          <>
            {loading ? (
              <div className="marketplace-loading">Loading marketplace...</div>
            ) : filteredItems.length === 0 ? (
              <div className="marketplace-empty">
                {searchLower ? `No items matching "${searchTerm.trim()}"` : 'No listings available yet.'}
              </div>
            ) : (
              <ItemGrid layout={thumbLayout} size={thumbSize}>
                {filteredItems.map((item: any) => {
                  const isDepot = item._type === 'depot';
                  const cardKey = isDepot ? `depot-${item.depot_id}-${item.id}` : `auction-${item.id}`;
                  const isHighlighted = !isDepot && item.id === highlightAuctionId;
                  return (
                    <MarketplaceItemCard
                      key={cardKey}
                      item={item}
                      size={thumbSize}
                      onClick={handlePublicItemClick}
                      onImageClick={handlePublicImageClick}
                      onDepotClick={handlePublicDepotClick}
                      onBidClick={!isDepot ? handlePublicBidClick : undefined}
                      isHighlighted={isHighlighted}
                      isSelected={
                        (isDepot && selectedItems.has(item.id)) ||
                        (item._hasBuyNow && item._depotItem && selectedItems.has(item._depotItem.id)) ||
                        (!isDepot && selectedItems.has(item.id))
                      }
                      highlightRef={isHighlighted ? highlightRef : (isDepot && linkItemIds[0] === item.id ? highlightRef : undefined)}
                      expandedDescs={expandedDescs}
                      onToggleDesc={toggleDesc}
                    />
                  );
                })}
              </ItemGrid>
            )}
          </>
        )}
      </div>

      {/* Floating bottom bar for depot item selection */}
      {selectedItems.size > 0 && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000,
          background: 'var(--color-bg-primary)', borderTop: '1px solid var(--color-border)',
          padding: '10px 16px 14px', display: 'flex', flexDirection: 'column', gap: '8px',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
        }}>
          {/* Row 1: count + clear */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
              {selectedItems.size} {selectedItems.size === 1 ? 'item' : 'items'} selected
            </span>
            <button
              onClick={() => setSelectedItems(new Set())}
              style={{
                background: 'transparent', border: '1px solid var(--color-border)', borderRadius: '6px',
                color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', cursor: 'pointer',
                padding: '4px 12px', fontWeight: 500,
              }}
            >Clear</button>
          </div>
          {/* Row 2: action buttons */}
          {(() => {
            // Check if any selected items are purchasable (depot or Buy Now)
            const allDepot = [...depotItems, ...drillDownItems];
            const hasPurchasable = allDepot.some(i => selectedItems.has(i.id)) || buyNowPrices.size > 0;
            return (
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => {
                    let depotId = selectedDepot?.depot_id;
                    if (!depotId) {
                      const selectedDepotIds = new Set(
                        allDepot.filter(i => selectedItems.has(i.id) && i.depot_id).map(i => i.depot_id)
                      );
                      if (selectedDepotIds.size === 1) depotId = Array.from(selectedDepotIds)[0];
                    }
                    const itemIds = Array.from(selectedItems).join(',');
                    const base = window.location.origin + '/marketplace';
                    const params = new URLSearchParams();
                    if (depotId) params.set('depot', String(depotId));
                    if (itemIds) params.set('item', itemIds);
                    const url = params.toString() ? `${base}?${params}` : base;
                    navigator.clipboard.writeText(url).then(() => {
                      setShowCopiedToast(true);
                      setTimeout(() => setShowCopiedToast(false), 2000);
                    }).catch(() => {
                      window.prompt('Copy this link:', url);
                    });
                  }}
                  style={{
                    flex: 1, padding: '10px 0', background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)',
                    border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: 'var(--font-size-sm)', fontWeight: 500,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                  </svg>
                  Share
                </button>
                {hasPurchasable && (
                  <button
                    onClick={openReviewSheet}
                    style={{
                      flex: 2, padding: '10px 0', background: 'var(--color-accent)', color: '#fff',
                      border: 'none', borderRadius: '8px', fontSize: 'var(--font-size-sm)', fontWeight: 600,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"></line>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                    </svg>
                    Contact Seller
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Copied toast */}
      {showCopiedToast && (
        <div style={{
          position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 2000,
          background: 'var(--color-text-primary)', color: 'var(--color-bg-primary)',
          padding: '10px 20px', borderRadius: '8px', fontSize: 'var(--font-size-sm)', fontWeight: 500,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          Link copied!
        </div>
      )}

      {/* Review sheet modal */}
      {showReviewSheet && createPortal(
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 9999, touchAction: 'none' }}
          onClick={() => setShowReviewSheet(false)}
          onTouchMove={(e) => e.preventDefault()}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            style={{ background: 'var(--color-bg-primary)', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 480, maxHeight: '80vh', overflow: 'auto', overscrollBehavior: 'contain', padding: '20px 16px', boxShadow: '0 -4px 20px rgba(0,0,0,0.15)' }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)', fontWeight: 600, color: 'var(--color-text-primary)' }}>Review Your Order</h3>
              <button onClick={() => setShowReviewSheet(false)} style={{ background: 'none', border: 'none', fontSize: '24px', color: 'var(--color-text-muted)', cursor: 'pointer', padding: '4px' }}>×</button>
            </div>

            {/* Items */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
              {reviewItems.map((item, idx) => (
                <div key={item.id} style={{
                  padding: '12px', borderRadius: '10px',
                  border: '1px solid var(--color-border)',
                  background: item.priceLocked ? 'rgba(22, 163, 106, 0.08)' : item.price !== item.originalPrice ? 'rgba(249, 115, 22, 0.08)' : 'var(--color-bg-secondary)',
                }}>
                  <div style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', marginBottom: '8px' }}>
                    #{item.id} — {item.name}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
                    {/* Qty stepper */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                      <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginRight: '2px' }}>Qty</label>
                      <button
                        onClick={() => setReviewItems(prev => prev.map((r, i) => i === idx ? { ...r, qty: Math.max(1, r.qty - 1) } : r))}
                        disabled={item.qty <= 1}
                        style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid var(--color-border)', background: item.qty <= 1 ? 'var(--color-bg-tertiary)' : 'var(--color-bg-primary)', color: item.qty <= 1 ? 'var(--color-text-faint)' : 'var(--color-text-primary)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: item.qty <= 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                      >−</button>
                      <input
                        type="text" inputMode="numeric" defaultValue={item.qty} key={`qty-${item.id}-${item.qty}`}
                        onBlur={(e) => { const val = Math.min(item.maxQty, Math.max(1, parseInt(e.target.value) || 1)); e.target.value = String(val); setReviewItems(prev => prev.map((r, i) => i === idx ? { ...r, qty: val } : r)); }}
                        style={{ width: '36px', padding: '4px 2px', borderRadius: '6px', border: '1px solid var(--color-border)', background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-sm)', textAlign: 'center' }}
                      />
                      <button
                        onClick={() => setReviewItems(prev => prev.map((r, i) => i === idx ? { ...r, qty: Math.min(r.maxQty, r.qty + 1) } : r))}
                        disabled={item.qty >= item.maxQty}
                        style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid var(--color-border)', background: item.qty >= item.maxQty ? 'var(--color-bg-tertiary)' : 'var(--color-bg-primary)', color: item.qty >= item.maxQty ? 'var(--color-text-faint)' : 'var(--color-text-primary)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: item.qty >= item.maxQty ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                      >+</button>
                      {item.maxQty > 1 && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginLeft: '2px' }}>/{item.maxQty}</span>}
                    </div>
                    {/* Price */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                      <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>$</label>
                      <input
                        type="text" inputMode="decimal" defaultValue={item.price.toFixed(2)} key={`price-${item.id}`}
                        disabled={!!item.priceLocked}
                        onBlur={(e) => { if (item.priceLocked) return; const val = Math.max(0, parseFloat(e.target.value) || 0); e.target.value = val.toFixed(2); setReviewItems(prev => prev.map((r, i) => i === idx ? { ...r, price: val } : r)); }}
                        style={{ width: '64px', padding: '6px 6px', borderRadius: '6px', border: `1px solid ${item.priceLocked ? 'var(--color-success)' : item.price !== item.originalPrice ? 'var(--color-warning)' : 'var(--color-border)'}`, background: item.priceLocked ? 'var(--color-bg-tertiary)' : 'var(--color-bg-primary)', color: item.priceLocked ? 'var(--color-text-muted)' : 'var(--color-text-primary)', fontSize: 'var(--font-size-sm)', textAlign: 'center' }}
                      />
                    </div>
                    {/* Subtotal */}
                    <span style={{ marginLeft: 'auto', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                      ${(item.qty * item.price).toFixed(2)}
                    </span>
                  </div>
                  {item.priceLocked ? (
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-success)', marginTop: '6px' }}>Buy Now price</div>
                  ) : item.price !== item.originalPrice ? (
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-warning)', marginTop: '6px' }}>Offer — listed at ${item.originalPrice.toFixed(2)}</div>
                  ) : null}
                </div>
              ))}
            </div>

            {/* Total + Send */}
            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--color-text-primary)' }}>Total</span>
                <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                  ${reviewItems.reduce((sum, i) => sum + i.price * i.qty, 0).toFixed(2)}
                </span>
              </div>
              <button
                onClick={() => {
                  // Use first item's seller info (items may span multiple sellers)
                  const first = reviewItems[0];
                  if (!first) return;
                  setContactSellerId(first.sellerId);
                  setContactSellerName(first.sellerName);
                  setContactDepotId(first.depotId);
                  setContactReviewItems(reviewItems.map(i => ({ id: i.id, name: i.name, qty: i.qty, maxQty: i.maxQty, price: i.price, originalPrice: i.originalPrice, priceLocked: i.priceLocked })));
                  setShowReviewSheet(false);
                  setShowContactModal(true);
                }}
                style={{ width: '100%', padding: '14px', background: 'var(--color-accent)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
                Contact Seller
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Contact Seller Modal */}
      <ContactSellerModal
        open={showContactModal}
        onClose={() => { setShowContactModal(false); setContactReviewItems(undefined); }}
        sellerId={contactSellerId}
        sellerName={contactSellerName}
        collectionId={contactDepotId}
        reviewItems={contactReviewItems}
      />

      {/* Sign-in prompt modal */}
      {showSignInPrompt && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => setShowSignInPrompt(false)}
        >
          <div
            style={{
              background: 'var(--color-bg-primary)',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '360px',
              width: '100%',
              textAlign: 'center',
              boxShadow: 'var(--shadow-lg, 0 10px 40px rgba(0,0,0,0.15))',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🧦</div>
            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: '8px' }}>
              Sign in to bid
            </div>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginBottom: '20px', lineHeight: 1.5 }}>
              Create a free Wandering Sock account to place bids, track auctions, and manage your marketplace.
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowSignInPrompt(false)}
                style={{
                  padding: '10px 20px', borderRadius: '10px',
                  border: '1px solid var(--color-border)', background: 'var(--color-bg-primary)',
                  color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: 'var(--font-size-sm)',
                }}
              >
                Maybe later
              </button>
              <button
                onClick={() => { setShowSignInPrompt(false); navigate('/'); }}
                style={{
                  padding: '10px 20px', borderRadius: '10px',
                  border: 'none', background: 'var(--color-accent)', color: '#fff',
                  cursor: 'pointer', fontSize: 'var(--font-size-sm)', fontWeight: 600,
                }}
              >
                Sign in
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Attribution Modal */}
      {attrImage && (
        <ImageAttributionModal
          isOpen={true}
          onClose={() => setAttrImage(null)}
          imageUrl={attrImage.url}
          itemName={attrImage.name}
          photographerName={attrImage.photographerName}
          photographerUsername={attrImage.photographerUsername}
          unsplashUrl={attrImage.unsplashPhotoId ? `https://unsplash.com/photos/${attrImage.unsplashPhotoId}` : undefined}
        />
      )}

      {/* Proximity help tip */}
      <ContextHelpTip tipId="market-proximity" show={!!maxDistance} position="bottom" bottomOffset={80} />

      {/* Proximity / Location Modal */}
      {showLocationModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => { setShowLocationModal(false); setLocationError(null); }}
        >
          <div
            style={{
              background: 'var(--color-bg-primary)',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '380px',
              width: '100%',
              boxShadow: 'var(--shadow-lg, 0 10px 40px rgba(0,0,0,0.15))',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Step 1: Get location (if not set) */}
            {!viewerLoc ? (
              <>
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '8px' }}>
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                    Set Your Location
                  </div>
                  <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                    To filter items by distance
                  </div>
                </div>

                <button
                  onClick={handleBrowserLocation}
                  disabled={locatingBrowser}
                  style={{
                    width: '100%', padding: '12px', marginBottom: '10px',
                    borderRadius: '10px', border: 'none',
                    background: 'var(--color-accent)', color: '#fff',
                    fontSize: 'var(--font-size-sm)', fontWeight: 600,
                    cursor: locatingBrowser ? 'wait' : 'pointer',
                    opacity: locatingBrowser ? 0.7 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="2" x2="12" y2="6"></line>
                    <line x1="12" y1="18" x2="12" y2="22"></line>
                    <line x1="2" y1="12" x2="6" y2="12"></line>
                    <line x1="18" y1="12" x2="22" y2="12"></line>
                  </svg>
                  {locatingBrowser ? 'Locating...' : 'Use My Location'}
                </button>

                <div style={{ textAlign: 'center', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', margin: '6px 0' }}>or</div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Zip code"
                    maxLength={5}
                    value={customDistanceInput}
                    onChange={(e) => setCustomDistanceInput(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleZipLookup(customDistanceInput); }}
                    style={{
                      flex: 1, padding: '10px 12px', borderRadius: '10px',
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-bg-secondary, var(--color-bg-primary))',
                      color: 'var(--color-text-primary)',
                      fontSize: 'var(--font-size-sm)',
                      outline: 'none',
                    }}
                  />
                  <button
                    onClick={() => handleZipLookup(customDistanceInput)}
                    disabled={locatingBrowser || customDistanceInput.length < 5}
                    style={{
                      padding: '10px 16px', borderRadius: '10px',
                      border: 'none', background: 'var(--color-accent)', color: '#fff',
                      fontSize: 'var(--font-size-sm)', fontWeight: 600,
                      cursor: 'pointer', opacity: customDistanceInput.length < 5 ? 0.5 : 1,
                    }}
                  >
                    Go
                  </button>
                </div>

                {locationError && (
                  <div style={{ marginTop: '10px', fontSize: 'var(--font-size-xs)', color: '#dc2626', textAlign: 'center' }}>
                    {locationError}
                  </div>
                )}
              </>
            ) : (
              /* Step 2: Pick distance (location already set) */
              <>
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                    Search Radius
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                    Show items within...
                  </div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', marginBottom: '12px' }}>
                  {DISTANCE_PRESETS.map((d) => (
                    <button
                      key={d}
                      onClick={() => selectDistance(d)}
                      style={{
                        padding: '8px 18px', borderRadius: '20px',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: maxDistance === d ? 700 : 500,
                        border: maxDistance === d ? '2px solid var(--color-accent)' : '1px solid var(--color-border)',
                        background: maxDistance === d ? 'var(--color-accent-light, #e0f0ff)' : 'var(--color-bg-primary)',
                        color: maxDistance === d ? 'var(--color-accent)' : 'var(--color-text-primary)',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {d} mi
                    </button>
                  ))}
                </div>

                {/* Custom distance */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Custom miles"
                    value={customDistanceInput}
                    onChange={(e) => setCustomDistanceInput(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => { if (e.key === 'Enter' && customDistanceInput) selectDistance(Number(customDistanceInput)); }}
                    style={{
                      flex: 1, padding: '10px 12px', borderRadius: '10px',
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-bg-secondary, var(--color-bg-primary))',
                      color: 'var(--color-text-primary)',
                      fontSize: 'var(--font-size-sm)',
                      outline: 'none',
                    }}
                  />
                  <button
                    onClick={() => { if (customDistanceInput) selectDistance(Number(customDistanceInput)); }}
                    disabled={!customDistanceInput}
                    style={{
                      padding: '10px 16px', borderRadius: '10px',
                      border: 'none', background: 'var(--color-accent)', color: '#fff',
                      fontSize: 'var(--font-size-sm)', fontWeight: 600,
                      cursor: 'pointer', opacity: customDistanceInput ? 1 : 0.5,
                    }}
                  >
                    Set
                  </button>
                </div>

                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <button
                    onClick={() => { setShowLocationModal(false); setCustomDistanceInput(''); }}
                    style={{
                      padding: '8px 16px', borderRadius: '10px',
                      border: '1px solid var(--color-border)', background: 'var(--color-bg-primary)',
                      color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: 'var(--font-size-xs)',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => { clearProximity(); setCustomDistanceInput(''); }}
                    style={{
                      padding: '8px 16px', borderRadius: '10px',
                      border: '1px solid var(--color-border)', background: 'var(--color-bg-primary)',
                      color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: 'var(--font-size-xs)',
                    }}
                  >
                    Change Location
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplacePage;
